import * as Sentry from '@sentry/react';
import { sentryConfig } from '../constants';
import { normalizeTransactionName } from './normalizeTransaction';

/**
 * Initialize Sentry with TanStack Router integration.
 * Must be called after the router is created, before rendering.
 *
 * @param router - The TanStack Router instance (typed as `any` to match Sentry's signature)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function initializeSentry(router: any) {
  const isTestEnvironment = import.meta.env.MODE === 'test' || import.meta.env.NODE_ENV === 'test';

  const client = Sentry.init({
    ...sentryConfig,
    integrations: [
      // Official TanStack Router integration handles pageload and navigation spans automatically
      // https://docs.sentry.io/platforms/javascript/guides/react/features/tanstack-router/
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      // Sends console.warn/console.error to Sentry's Logs product as
      // standalone, searchable log entries. Deliberately not using
      // captureConsoleIntegration, which would instead create a Sentry
      // Issue (exception-level event) for every console.error call.
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    // The TanStack Router integration names transactions after the matched
    // routeId, which is prefixed `/_protected/...` by our pathless auth layout
    // (and is the bare `/_protected` layout when an error fires before a leaf
    // route commits — the WorkOS redirect / notFound funnel). Normalize the
    // `transaction` field on every outgoing event so issues point at the
    // user-facing route instead of internal routing structure. See
    // `normalizeTransaction` for the full rationale.
    beforeSend(event) {
      if (event.transaction) {
        let fallbackPathname: string | undefined;
        const url = event.request?.url;
        if (url) {
          try {
            fallbackPathname = new URL(url).pathname;
          } catch {
            // Malformed request URL — leave fallback undefined.
          }
        }
        const normalized = normalizeTransactionName(event.transaction, fallbackPathname);
        if (normalized) event.transaction = normalized;
      }
      return event;
    },
    ignoreErrors: [
      // View Transition API races: when navigation animations are interrupted
      // (a new transition starts before the previous finishes, the page is
      // hidden, or a DOM update times out) the browser rejects the transition
      // as an AbortError/InvalidStateError/TimeoutError. These are benign — the
      // user still reaches the destination, just without the animation. The
      // exact message text varies by browser and version (e.g. "View transition
      // was skipped", "Transition was skipped", "Skipped ViewTransition due to
      // another transition starting"), so match loosely rather than per-string.
      /view ?transition/i,
      /Transition was (skipped|aborted)/i,
      // Page hidden mid-operation (tab switch / backgrounding): also benign.
      /visibility state (has become|is) hidden/,
    ],
  });

  // Only warn in non-test environments
  if (!isTestEnvironment && !client) {
    console.error('Sentry client is not initialized');
  }

  return client;
}
